// Photo/document picking + upload orchestration for the Pictures & Documents
// feature. Builds on storage.js (raw upload/delete helpers).
//
// The native pickers (expo-image-picker / expo-image-manipulator) only exist in
// builds 7+ — older binaries receive this code via OTA, so every native require
// is guarded and the UI should gate on isPhotoPickingAvailable().

import { Platform } from 'react-native';
import { auth } from './config';
import { uploadFileAsync } from './storage';

// Upload a business logo (invoices) under the user's own storage path.
export const uploadBusinessLogoAsync = async (uid, fileOrUri, mime = 'image/jpeg') => {
  const ext = mime.includes('png') ? 'png' : 'jpg';
  const path = `businessLogos/${uid}/logo_${Date.now()}.${ext}`;
  const url = await uploadFileAsync(path, fileOrUri);
  return { url, path };
};

let ImagePicker = null;
let ImageManipulator = null;
try {
  ImagePicker = require('expo-image-picker');
  ImageManipulator = require('expo-image-manipulator');
} catch {
  // Native modules not in this binary (pre-photo-feature build) — feature hides.
}

let DocumentPicker = null;
try {
  DocumentPicker = require('expo-document-picker');
} catch {}

// Photos are kept high quality: longest edge 2048px, JPEG 0.85 (~0.8–1.5 MB).
// Enough to zoom into an injury or read a photographed document.
const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.85;

export const isPhotoPickingAvailable = () =>
  Platform.OS === 'web' || (!!ImagePicker && !!ImageManipulator);

export const isDocPickingAvailable = () => Platform.OS === 'web' || !!DocumentPicker;

// Downscale so the longest edge is MAX_EDGE (never upscales), re-encode as JPEG.
const processPhoto = async (asset) => {
  if (Platform.OS === 'web' || !ImageManipulator) return asset.uri;
  const { width = 0, height = 0 } = asset;
  const longest = Math.max(width, height);
  const actions = longest > MAX_EDGE
    ? [width >= height ? { resize: { width: MAX_EDGE } } : { resize: { height: MAX_EDGE } }]
    : [];
  const result = await ImageManipulator.manipulateAsync(asset.uri, actions, {
    compress: JPEG_QUALITY,
    format: ImageManipulator.SaveFormat.JPEG,
  });
  return result.uri;
};

// Thrown when the OS permission for camera/library is denied, so the UI can
// show a helpful "enable it in Settings" message rather than failing silently.
export class PermissionDeniedError extends Error {
  constructor(kind) {
    super(kind === 'camera'
      ? 'Camera access is off. Enable it for Horse Math in your device Settings, then try again.'
      : 'Photo access is off. Enable it for Horse Math in your device Settings, then try again.');
    this.name = 'PermissionDeniedError';
  }
}

// Ask for photos from the library (multiple allowed) or the camera.
// Returns an array of processed local URIs ([] only when the user cancels).
// Throws PermissionDeniedError if access is off, or a real Error on failure —
// callers surface these instead of leaving the user with a dead button.
export const pickPhotosAsync = async (source = 'library') => {
  if (!ImagePicker) throw new Error('Photo picker is unavailable in this app version.');
  if (source === 'camera') {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) throw new PermissionDeniedError('camera');
    const res = await ImagePicker.launchCameraAsync({ quality: 1 });
    if (res.canceled || !res.assets?.length) return [];
    return [await processPhoto(res.assets[0])];
  }
  const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!perm.granted) throw new PermissionDeniedError('library');
  const res = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    allowsMultipleSelection: true,
    selectionLimit: 10,
    quality: 1,
  });
  if (res.canceled || !res.assets?.length) return [];
  return Promise.all(res.assets.map(processPhoto));
};

// Web photo picking: a hidden <input type=file> so no native module is needed.
export const pickPhotosWebAsync = () =>
  new Promise((resolve) => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return resolve([]);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = () => resolve(Array.from(input.files || []));
    // Some browsers never fire onchange for a cancelled dialog — that just
    // leaves an unresolved promise on a dead code path, which is harmless.
    input.click();
  });

// Pick a single document (PDF or image). Returns { uri|file, name, mime, size } or null.
export const pickDocumentAsync = async () => {
  if (Platform.OS === 'web') {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') return resolve(null);
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*,application/pdf';
      input.onchange = () => {
        const f = input.files && input.files[0];
        resolve(f ? { file: f, name: f.name, mime: f.type, size: f.size } : null);
      };
      input.click();
    });
  }
  if (!DocumentPicker) return null;
  const res = await DocumentPicker.getDocumentAsync({
    type: ['image/*', 'application/pdf'],
    copyToCacheDirectory: true,
  });
  if (res.canceled || !res.assets?.length) return null;
  const a = res.assets[0];
  return { uri: a.uri, name: a.name || 'document', mime: a.mimeType || '', size: a.size || 0 };
};

// Upload one photo/file for a horse and return the metadata object we store in
// Firestore arrays: { url, path, name, mime, size, docType?, uploadedBy, uploadedAt }.
// `kind` becomes the storage subfolder: 'documents' | 'incidents'.
export const uploadHorseFileAsync = async (horseId, fileOrUri, { kind = 'documents', name = '', mime = 'image/jpeg' } = {}) => {
  const ts = Date.now();
  const safeName = (name || `photo_${ts}.jpg`).replace(/[^\w.\-]+/g, '_').slice(-80);
  const path = `horses/${horseId}/${kind}/${ts}_${safeName}`;
  const url = await uploadFileAsync(path, fileOrUri);
  const user = auth.currentUser;
  return {
    url,
    path,
    name: safeName,
    mime,
    uploadedBy: user?.email || user?.uid || '',
    uploadedAt: new Date().toISOString(),
  };
};
